import { BLOCK_SHAPES, BLOCK_SIZE, MATERIALS, WORLD } from './constants.js'

export const DAMAGE_TUNING_DEFAULTS = {
    // Calibrated with the balance lab: a well-aimed shot with the matching
    // bullet (impact speed about 4) removes about 80% of a block's health;
    // slow lobs only chip. Collapses and falls finish the job.
    damageScale: 66,
    flagDirectDamageScale: 1,
    // Collision damage between blocks, ground and flag: a stone square
    // dropping two block-heights takes about a quarter of its health.
    collisionDamageScale: 680
}

const damageTuning = { ...DAMAGE_TUNING_DEFAULTS }
// Contacts slower than this (step units) are settling, not impacts.
const MIN_COLLISION_SPEED = 0.5
// The flag counts as moderately tough when things fall on it.
const FLAG_COLLISION_TOUGHNESS = 1

export function getDamageTuning() {
    return { ...damageTuning }
}

export function setDamageTuningValue(key, value) {
    if (!Object.hasOwn(DAMAGE_TUNING_DEFAULTS, key)) {
        return false
    }

    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) {
        return false
    }

    damageTuning[key] = numericValue
    return true
}

export function resetDamageTuning() {
    Object.assign(damageTuning, DAMAGE_TUNING_DEFAULTS)
}

export function getBlockMaxHealth(block) {
    const material = getMaterial(block?.material)
    const shape = BLOCK_SHAPES[block?.shape] || inferShape(block)
    const width = Number(block?.width || shape.width || BLOCK_SIZE.width)
    const height = Number(block?.height || shape.height || BLOCK_SIZE.height)
    const gridArea = Math.max(0.2, (width * height) / (WORLD.grid * WORLD.grid))
    const areaScale = Math.sqrt(gridArea / 4)
    const shapeScale = shape.healthMultiplier ?? 1

    return Math.max(8, Math.round((material.baseHealth || material.health || 80) * areaScale * shapeScale))
}

export function calculateImpactDamage({
    bulletConfig,
    bulletMass,
    speed,
    targetMaterial,
    normalFactor = 1
}) {
    const material = getMaterial(targetMaterial)
    const clampedSpeed = clamp(Number(speed) || 0, 0, 3.4)
    const mass = Math.max(0.0001, Number(bulletMass) || 0.0001)
    const impactEnergy = 0.5 * mass * clampedSpeed * clampedSpeed
    const matchup = getMaterialMultiplier(bulletConfig, targetMaterial)
    const toughness = Math.max(0.2, material.toughness || 1)
    const contactQuality = clamp(normalFactor, 0.25, 1.2)
    const shatterBonus = impactEnergy > (material.shatterThreshold || 0.02)
        ? 1 + (material.brittleness || 0) * 0.65
        : 1
    const projectileStrength = clamp((Number(bulletConfig?.impactStrength) || 50) / 50, 0.75, 1.45)
    const damage = impactEnergy * damageTuning.damageScale * projectileStrength * matchup * contactQuality * shatterBonus / toughness

    if (
        material.brittleImpactDamageMultiplier
        && clampedSpeed >= (material.brittleImpactSpeed || 0.6)
    ) {
        const cleanHitFactor = clamp((contactQuality - 0.35) / 0.65, 0, 1)
        const brittleFloor = (material.baseHealth || material.health || 24)
            * material.brittleImpactDamageMultiplier
            * cleanHitFactor

        return Math.max(damage, brittleFloor)
    }

    return damage
}

export function calculateFlagImpactDamage({ bulletConfig, bulletMass, speed, normalFactor = 1 }) {
    const baseDamage = calculateImpactDamage({
        bulletConfig,
        bulletMass,
        speed,
        targetMaterial: 'wood',
        normalFactor
    })

    return baseDamage * damageTuning.flagDirectDamageScale
}

// Damage a body takes from a collision: the impact energy of whatever hit it
// (its mass, or the body's own mass when it hits the ground), scaled down by
// the target's toughness and up by its fragility.
export function calculateCollisionDamage({ sourceMass, targetMaterial, relativeSpeed }) {
    const speed = Number(relativeSpeed) || 0
    if (speed < MIN_COLLISION_SPEED) {
        return 0
    }

    const isFlag = targetMaterial === 'flag'
    const profile = getMaterial(targetMaterial)
    const toughness = isFlag ? FLAG_COLLISION_TOUGHNESS : Math.max(0.2, profile.toughness || 1)
    const fragility = isFlag ? 1 : profile.fallDamage ?? 1
    const mass = Math.max(0.0001, Number(sourceMass) || 0)
    const energy = 0.5 * mass * (speed * speed - MIN_COLLISION_SPEED * MIN_COLLISION_SPEED)

    return energy * damageTuning.collisionDamageScale * fragility / toughness
}

export function getProjectileMass(config) {
    const radius = config?.radius || 0.32
    const density = config?.density || 0.018

    return Math.PI * radius * radius * density
}

function getMaterialMultiplier(config, material) {
    if (config?.materialMultipliers?.[material] !== undefined) {
        return config.materialMultipliers[material]
    }

    return config?.bonusAgainst === material
        ? config.materialMultiplier || 1.5
        : config?.offMaterialMultiplier || 0.5
}

function getMaterial(material) {
    return MATERIALS[material] || MATERIALS.wood
}

function inferShape(block) {
    const width = Number(block?.width || BLOCK_SIZE.width)
    const height = Number(block?.height || BLOCK_SIZE.height)
    const match = Object.values(BLOCK_SHAPES).find((shape) => (
        Math.abs(shape.width - width) < 0.001
        && Math.abs(shape.height - height) < 0.001
    ))

    return match || BLOCK_SHAPES.rectangle
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}
